import { makeAutoObservable, runInAction } from 'mobx';
import { Act, CombinedAct, CombinedActStateCodes, DocTypeCodes, Scan } from '@shared/types/osi/acts';
import {
  getBase64ByScanId,
  getCombinedActById,
  getCombinedActForm,
  getCombinedActsByOsiId,
  getDocsByActId,
  getNotSignedActsByOsiId,
  getSignedActsByOsiId,
  getWorkCompletionAct,
  saveSignedCombinedAct,
  saveSignedWorkCompletionAct
} from '@shared/api/osiActs';
import logger from 'js-logger';
import i18next from 'i18next';
import { notifications } from '@shared/utils/notifications';
import { inject, injectable } from 'inversify';
import { downloadBase64File, extractPdfFromCms, openBase64PdfInNewWindow } from '@shared/utils/helpers/pdfHelpers';
import { MIME } from '@shared/utils/download';
import { hasSessionToken } from '@shared/api/session';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';

interface SignedActsDialog {
  doc: {
    fileName: string;
    base64: string;
  } | null;
  isOpen: boolean;
  act: Act | null;
  /** OSI-763: id скана с подписанным контейнером — нужен кнопке «Сохранить». null — скана нет. */
  scanId: number | null;
  /** OSI-763: имя файла скана как оно лежит на бэкенде (запасной вариант имени при выгрузке). */
  scanFileName: string;
  /**
   * OSI-763: открыто ли подтверждение «подписанного файла нет, скачать печатную форму?».
   * Лежит именно здесь, а не отдельным полем класса, чтобы сбрасываться вместе со всем
   * диалогом в `onCloseSignedActsDialog` ({ ...initialSignedActsDialog }) — иначе
   * подтверждение «переехало» бы на следующий открытый акт.
   */
  isNoScanConfirmOpen: boolean;
  /**
   * OSI-763: кэш печатной формы акта (base64) на время жизни диалога.
   *
   * Печатная форма нужна двум сценариям — «Печать» и «Сохранить без ЭЦП» — и грузится
   * ЛЕНИВО, по первому обращению: при открытии акта со сканом в просмотр идёт оригинал,
   * и запрос за формой был бы напрасным. Кэш живёт здесь, а не отдельным полем класса,
   * чтобы обнуляться вместе с диалогом в `onCloseSignedActsDialog` — иначе следующий
   * открытый акт получил бы чужую печатную форму.
   */
  printFormBase64: string | null;
  /**
   * OSI-763: в просмотре показана печатная форма, а не оригинал (у акта нет скана).
   * Управляет подписью под областью просмотра: тексты у этих двух случаев разные.
   */
  isPrintFormShown: boolean;
}

const initialSignedActsDialog: SignedActsDialog = {
  doc: null,
  isOpen: false,
  act: null,
  scanId: null,
  scanFileName: '',
  isNoScanConfirmOpen: false,
  printFormBase64: null,
  isPrintFormShown: false
};

interface NotSignedActsDialog {
  doc: {
    fileName: string;
    base64: string;
  } | null;
  isOpen: boolean;
  act: Act | null;
}

/**
 * OSI-763: признак «сессия уже завершена по HTTP 401».
 *
 * Response-интерцептор (`shared/api/config.ts`) на 401 сам показывает тост
 * «Сессия истекла», вызывает `sessionExpired()` (очистка сессии + редирект на
 * /auth/check-contact) и делает `return;` БЕЗ throw. Из-за этого промис вызывающего
 * кода РЕЗОЛВИТСЯ значением `undefined`, а не отклоняется, и сценарий продолжает
 * выполняться как при успехе — вплоть до ложного зелёного «Акт успешно подписан».
 *
 * `sessionExpired()` затирает токен СИНХРОННО (`authModule.sessionExpired()` →
 * `localStorage.setItem('token', '')`, `mobx/services/auth/index.ts:64-67`), то есть
 * к моменту продолжения нашего `await` токена в localStorage уже нет.
 * Поэтому после каждого запроса проверяем признак и выходим МОЛЧА: тост и редирект
 * интерцептор уже сделал, второй тост только вводил бы пользователя в заблуждение.
 *
 * Слияние редакции 2: сценарий стал однофазным, но признак обязан остаться —
 * проверки из удалённых методов двухфазного подписания перенесены в `onPostSign`.
 */
const isSessionGone = (): boolean => !hasSessionToken();

/**
 * OSI-763: интерцептор (`shared/api/config.ts`) при code !== 0 бросает СТРОКУ с сообщением
 * бэкенда. Показываем её как есть через toast — notifications.error() прогнал бы аргумент
 * через i18next.t и на сыром тексте дал бы missingKey. Во всех остальных случаях
 * (сеть, неожиданное исключение) — свой i18n-ключ.
 */
const notifySignError = (e: unknown, fallbackToken: string) => {
  // OSI-763: сессия завершена по 401 — «Сессия истекла» уже показана интерцептором, молчим.
  if (isSessionGone()) return;

  if (typeof e === 'string' && e.trim()) {
    notifications.toast(e, 'error');
    return;
  }

  notifications.error(fallbackToken);
};

const initialNotSignedActsDialog: NotSignedActsDialog = {
  doc: null,
  isOpen: false,
  act: null
};

/** OSI-818: диалог подписания общего акта по БИН/ИИН. */
interface CombinedActDialog {
  isOpen: boolean;
  combinedAct: CombinedAct | null;
  /** PDF неподписанного общего акта (base64) — его и подписывает SignerBasic. */
  doc: { base64: string } | null;
}

const initialCombinedActDialog: CombinedActDialog = {
  isOpen: false,
  combinedAct: null,
  doc: null
};

@injectable()
export class OsiActsWidgetViewModel {
  /**
   * OSI-818: число загрузок списков, которые сейчас в полёте.
   * Раньше был один булев isLoading, и каждый загрузчик в finally сбрасывал его в false.
   * С тремя параллельными загрузчиками (подписанные, неподписанные, общие акты) первый
   * завершившийся снимал бы флаг у остальных, и страница показывалась бы недогруженной.
   */
  pendingLoads = 0;

  signedActs: Act[] = [];

  notSignedActs: Act[] = [];

  /** OSI-818: общие акты по БИН/ИИН, в которые входит акт текущего дома (CREATED и SIGNED). */
  combinedActs: CombinedAct[] = [];

  signedActsDialog: SignedActsDialog = { ...initialSignedActsDialog };

  notSignedActsDialog: NotSignedActsDialog = { ...initialNotSignedActsDialog };

  /** OSI-818: диалог подписания общего акта. */
  combinedActDialog: CombinedActDialog = { ...initialCombinedActDialog };

  /**
   * OSI-818: id общего акта, который сейчас открывается (запрос PDF может идти до 3 минут).
   * Нужен, чтобы заблокировать кнопки и не отправить запрос повторно. Общий isLoading
   * здесь не подходит: он подменяет всю страницу на LoadingScreen.
   */
  openingCombinedActId: number | null = null;

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);

    void this.loadSignedActs();
    void this.loadNotSignedActs();
    // OSI-818: общие акты грузятся параллельно с остальными списками.
    void this.loadCombinedActs();
  }

  /** OSI-818: страница в загрузке, пока не завершились все загрузчики (см. pendingLoads). */
  get isLoading(): boolean {
    return this.pendingLoads > 0;
  }

  get osiAddress(): string {
    return this.osiModule.osiInfo?.address || '';
  }

  /** OSI-818: id текущего дома — по нему в составе общего акта ищется «свой» акт. */
  get currentOsiId(): number | null {
    return this.osiModule.osiInfo?.id ?? null;
  }

  /** OSI-818: общие акты, ожидающие подписи. */
  get notSignedCombinedActs(): CombinedAct[] {
    return this.combinedActs.filter((item) => item.stateCode === CombinedActStateCodes.CREATED);
  }

  /** OSI-818: подписанные общие акты. */
  get signedCombinedActs(): CombinedAct[] {
    return this.combinedActs.filter((item) => item.stateCode === CombinedActStateCodes.SIGNED);
  }

  /** OSI-818: общий акт, в состав которого входит открытый подписанный акт (заголовок и подсказка диалога). */
  get signedDialogCombinedAct(): CombinedAct | null {
    const combinedActId = this.signedActsDialog.act?.combinedActId;

    if (!combinedActId) return null;

    return this.combinedActs.find((item) => item.id === combinedActId) ?? null;
  }

  /** OSI-818: счётчик активных загрузок (см. pendingLoads). */
  private startLoad = () => {
    this.pendingLoads += 1;
  };

  private finishLoad = () => {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
  };

  loadSignedActs = async () => {
    try {
      // OSI-818: счётчик вместо булева флага (см. pendingLoads).
      this.startLoad();
      if (!this.osiModule.osiInfo?.id) return;

      const signedActs = await getSignedActsByOsiId(this.osiModule.osiInfo.id);
      runInAction(() => {
        this.signedActs = signedActs;
      });
    } catch (e) {
      logger.error(e);
      // OSI-763: вместо безликой «Ошибка» — конкретный i18n-ключ.
      notifications.error('acts:errors.loadSignedActsFailed');
    } finally {
      this.finishLoad();
    }
  };

  loadNotSignedActs = async () => {
    try {
      // OSI-818: счётчик вместо булева флага (см. pendingLoads).
      this.startLoad();
      if (!this.osiModule.osiInfo?.id) return;

      const notSignedActs = await getNotSignedActsByOsiId(this.osiModule.osiInfo.id);
      runInAction(() => {
        this.notSignedActs = notSignedActs;
      });
    } catch (e) {
      logger.error(e);
      notifications.error('acts:errors.loadNotSignedActsFailed');
    } finally {
      this.finishLoad();
    }
  };

  /**
   * OSI-818: общие акты по БИН/ИИН для текущего дома. Один запрос без `state` —
   * бэкенд отдаёт и CREATED, и SIGNED; раскладка по статусам — в геттерах.
   * Ошибка НЕ блокирующая: обычные акты остаются рабочими, пользователь видит тост.
   */
  loadCombinedActs = async () => {
    try {
      this.startLoad();
      if (!this.osiModule.osiInfo?.id) return;

      const combinedActs = await getCombinedActsByOsiId(this.osiModule.osiInfo.id);
      runInAction(() => {
        // На 401 запрос резолвится undefined (см. isSessionGone) — оставляем массив.
        this.combinedActs = combinedActs ?? [];
      });
    } catch (e) {
      logger.error(e);
      notifications.error('acts:errors.loadCombinedActsFailed');
    } finally {
      this.finishLoad();
    }
  };

  /** OSI-818: перезагрузка всех трёх списков; сбой одного не мешает остальным. */
  private reloadAllActs = async (): Promise<void> => {
    await Promise.allSettled([this.loadNotSignedActs(), this.loadSignedActs(), this.loadCombinedActs()]);
  };

  /**
   * OSI-818: базовое имя файла ПОДПИСАННОГО акта (без расширения) для просмотра и «Сохранить».
   *
   * У акта из общего акта скан ОДИН на весь общий акт — внутри акты всех домов группы,
   * поэтому имя по номеру акта своего дома вводило бы в заблуждение. Для таких актов
   * имя `Общий_акт_<YYYY-MM>_<БИН/ИИН>`, префикс — из i18n. Печатная форма («Печать»,
   * «Сохранить без ЭЦП») — это акт своего дома, её имя по-прежнему от номера акта.
   */
  private getSignedFileBaseName = (act: Act | null | undefined): string => {
    if (!act?.combinedActId) return act?.actNum ?? '';

    const idn = this.combinedActs.find((item) => item.id === act.combinedActId)?.idn || act.osiIdn || '';
    const period = (act.actPeriod ?? '').slice(0, 7);
    const prefix = i18next.t('acts:combinedAct.fileNamePrefix');

    return [prefix, period, idn].filter(Boolean).join('_');
  };

  /**
   * OSI-763 (редакция «просмотр = оригинал»): в просмотр идёт САМ ПОДПИСАННЫЙ ДОКУМЕНТ
   * из скана (`GET /core/api/Scans/{id}`), а не печатная форма.
   *
   * Раньше здесь безусловно грузилась печатная форма (`GET /doc/api/act/work-completion/{id}`):
   * пользователь видел на экране одно, а «Сохранить» отдавало другое. Теперь на экране
   * ровно тот файл, который скачается, а печатная форма с расшифровкой ЭЦП выходит
   * только по кнопке «Печать» и грузится лениво (см. `loadPrintForm`).
   *
   * Исключение — исторические акты без документа SIGNED_ACT (на проде это 229, 231, 429, 1995):
   * показывать нечего, поэтому в просмотр идёт печатная форма, она же сразу кладётся в кэш
   * (второй раз её не запрашиваем), а под областью просмотра выводится пометка, что это
   * не подписанный файл — за это отвечает `isPrintFormShown`.
   */
  onShowSignedActClick = async (actId: number): Promise<void> => {
    // TODO: add loading state to all buttons
    try {
      const act = this.signedActs?.find((item) => item.id === actId);

      if (!act) {
        notifications.error('acts:errors.actNotFound');
        return;
      }

      // Скан ищем «мягко»: сбой этого запроса не должен закрывать просмотр — акт
      // откроется по запасному сценарию, печатной формой.
      // OSI-763: фильтр по SIGNED_ACT обязателен. К акту можно приложить произвольные
      // документы (POST /api/Acts/{id}/docs принимает любой docTypeCode), и они лежат
      // в тех же act_docs, а порядок выдачи ничем не задан. Без фильтра в просмотр
      // и в выгрузку мог бы попасть посторонний файл под именем <actNum>.pdf.
      let scan: Scan | null = null;
      try {
        const docs = await getDocsByActId(actId);
        scan = docs?.find((item) => item.docTypeCode === DocTypeCodes.SIGNED_ACT && item.scan?.id)?.scan ?? null;
      } catch (e) {
        logger.error(e);
      }

      // OSI-763: на 401 запрос резолвится undefined (см. isSessionGone). Проверка стоит
      // ПОСЛЕ try/catch, чтобы отработать одинаково и на успехе, и на проглоченной ошибке:
      // иначе поверх «Сессия истекла» выскочил бы второй тост.
      if (isSessionGone()) return;

      const scanId = scan?.id ?? null;
      const scanFileName = scan?.fileName ?? '';

      if (scanId) {
        const base64 = await getBase64ByScanId(scanId);

        if (isSessionGone()) return;

        if (!base64) {
          notifications.error('acts:errors.loadScanFailed');
          return;
        }

        // OSI-763: в ПРОСМОТР идёт PDF, ИЗВЛЕЧЁННЫЙ из CMS-контейнера, а не контейнер целиком.
        // На контейнере pdf.js вешает рендерер вкладки — воспроизведено на акте 22936
        // (подробности и структура файла — в комментарии к extractPdfFromCms).
        // Извлечение затрагивает ТОЛЬКО просмотр: «Сохранить» (onDownloadAct) грузит скан
        // заново и отдаёт полный контейнер, юридическую силу имеет именно он.
        const viewBase64 = extractPdfFromCms(base64);

        runInAction(() => {
          this.signedActsDialog = {
            // Имя строим от номера акта: в скане лежит .cms, и его имя пользователю
            // показывать незачем. Расширение всегда .pdf — сигнатура %PDF- лежит
            // в контейнере со смещением ~65 байт, и ридеры такой файл открывают.
            // OSI-818: у акта из общего акта скан общий — имя `Общий_акт_…` (см. getSignedFileBaseName).
            doc: { fileName: `${this.getSignedFileBaseName(act)}.pdf`, base64: viewBase64 },
            isOpen: true,
            act,
            scanId,
            scanFileName,
            isNoScanConfirmOpen: false,
            // Печатную форму НЕ трогаем: она нужна только «Печати» и подгрузится по клику.
            printFormBase64: null,
            isPrintFormShown: false
          };
        });
        return;
      }

      // Скана нет — запасной сценарий: показываем печатную форму и честно об этом пишем.
      const data = await getWorkCompletionAct(actId);

      if (isSessionGone()) return;

      if (!data?.pdfBase64) {
        notifications.error('acts:errors.loadFormFailed');
        return;
      }

      runInAction(() => {
        this.signedActsDialog = {
          doc: { fileName: `${act.actNum}.pdf`, base64: data.pdfBase64 },
          isOpen: true,
          act,
          scanId: null,
          scanFileName,
          isNoScanConfirmOpen: false,
          // Форма уже загружена — сразу в кэш, чтобы «Печать» и «Сохранить без ЭЦП»
          // не сходили за ней второй раз.
          printFormBase64: data.pdfBase64,
          isPrintFormShown: true
        };
      });
    } catch (e) {
      logger.error(e);
      notifySignError(e, 'acts:errors.loadFormFailed');
    }
  };

  onCloseSignedActsDialog = () => {
    this.signedActsDialog = { ...initialSignedActsDialog };
  };

  /**
   * OSI-763: единая ленивая загрузка печатной формы подписанного акта с кэшем на диалог.
   *
   * Форму просят два сценария — «Печать» и «Сохранить без ЭЦП», — и оба обязаны получать
   * именно её, а не то, что сейчас в просмотре (там теперь лежит оригинал из скана).
   * Первый вызов ходит на `GET /doc/api/act/work-completion/{id}`, все последующие
   * отдают кэш `signedActsDialog.printFormBase64` без сетевого запроса. Если акт открылся
   * по сценарию «скана нет», форма уже в кэше и запроса не будет вовсе.
   *
   * Возвращает null, если получить форму не удалось; о причине пользователь уже уведомлён.
   */
  private loadPrintForm = async (): Promise<string | null> => {
    const cached = this.signedActsDialog.printFormBase64;

    if (cached) return cached;

    const actId = this.signedActsDialog.act?.id;

    if (!actId) {
      notifications.error('acts:errors.actNotFound');
      return null;
    }

    try {
      const data = await getWorkCompletionAct(actId);

      // OSI-763: на 401 запрос резолвится undefined (см. isSessionGone) — без проверки
      // поверх «Сессия истекла» выскочил бы второй тост про ошибку загрузки формы.
      if (isSessionGone()) return null;

      if (!data?.pdfBase64) {
        notifications.error('acts:errors.loadFormFailed');
        return null;
      }

      runInAction(() => {
        this.signedActsDialog.printFormBase64 = data.pdfBase64;
      });

      return data.pdfBase64;
    } catch (e) {
      logger.error(e);
      notifySignError(e, 'acts:errors.loadFormFailed');
      return null;
    }
  };

  /**
   * OSI-763: печатается ПЕЧАТНАЯ ФОРМА — единственный артефакт, где есть расшифровка
   * ключей ЭЦП (номера сертификатов, ИИН/БИН и ФИО подписантов). В просмотре при этом
   * показан оригинал из скана: расшифровки в нём нет и появиться не может — подписывался
   * чистый PDF, а любая правка байтов сломала бы подписи.
   *
   * Метод асинхронный; MUI-кнопке это безразлично — `onClick` типизирован как
   * `React.MouseEventHandler`, возвращаемое значение не используется (в диалоге уже
   * есть такой же асинхронный `onDownloadAct`).
   */
  onPrintAct = async (): Promise<void> => {
    const base64 = await this.loadPrintForm();

    if (!base64) return;

    openBase64PdfInNewWindow(base64);
  };

  /**
   * OSI-763: «Сохранить» отдаёт ПОЛНЫЙ CMS-контейнер из скана (`GET /core/api/Scans/{id}`)
   * байт-в-байт, без каких-либо преобразований — юридическую силу имеет именно он.
   *
   * ВАЖНО: скан грузится ЗДЕСЬ ЗАНОВО, а не берётся из `signedActsDialog.doc.base64`.
   * В просмотре лежит PDF, ИЗВЛЕЧЁННЫЙ из контейнера (`extractPdfFromCms`) — иначе pdf.js
   * вешает рендерер вкладки. Подставить сюда содержимое просмотра нельзя: пользователь
   * получил бы файл без подписей. Это два разных пути, и они обязаны такими остаться.
   *
   * С ПЕЧАТЬЮ выгрузка не совпадает тем более: на печать уходит печатная форма от
   * `GET /doc/api/act/work-completion/{id}`, куда бэкенд дорисовывает подвал с
   * расшифровкой ключей ЭЦП (номера сертификатов, ИИН/БИН и ФИО подписантов).
   *
   * Это РАЗНЫЕ файлы, и совместить их нельзя: внутри контейнера лежит чистый PDF без
   * подвала (подписывали его до того, как подвал появился), а дописать подвал в контейнер
   * невозможно — любое изменение байтов сломало бы подписи.
   *
   * Расширение выгрузки — `.pdf`, а не `.cms`: сигнатура `%PDF-` лежит в контейнере
   * со смещением ~65 байт, а PDF-ридеры ищут её в первых 1024 байтах, поэтому контейнер
   * открывается обычной «читалкой». Пользователь получает привычный .pdf, который при
   * этом остаётся юридически значимым.
   */
  onDownloadAct = async (): Promise<void> => {
    const { scanId, scanFileName, act } = this.signedActsDialog;

    // OSI-763: у исторических актов документа SIGNED_ACT нет (на проде это акты 229, 231,
    // 429, 1995) — скачивать нечего. Раньше кнопка в этом случае была просто disabled:
    // пользователь видел серую кнопку без объяснения и не получал вообще ничего.
    // Теперь спрашиваем. Молча подсунуть вместо подписанного акта печатную форму нельзя:
    // она без ЭЦП и юридической силы не имеет, а по имени файла в «Загрузках» её от
    // подписанного акта не отличить. Значит, согласие пользователь даёт осознанно.
    if (!scanId) {
      // Присваивание синхронное и до первого await, поэтому runInAction не нужен —
      // makeAutoObservable уже сделал метод action (так же, как onCloseSignedActsDialog).
      this.signedActsDialog.isNoScanConfirmOpen = true;
      return;
    }

    try {
      const base64 = await getBase64ByScanId(scanId);

      // OSI-763: на 401 запрос резолвится undefined (см. isSessionGone) — без проверки
      // скачивание упало бы на atob(undefined) и дало второй тост поверх «Сессия истекла».
      if (isSessionGone()) return;

      // Имя строим от номера акта; если его вдруг нет — от имени скана без его исходного
      // расширения (в скане лежит .cms). Расширение всегда принудительно .pdf.
      // OSI-818: для акта из общего акта скачивается общий подписанный документ —
      // имя `Общий_акт_<YYYY-MM>_<БИН/ИИН>.pdf` (см. getSignedFileBaseName).
      const baseName = this.getSignedFileBaseName(act) || scanFileName.replace(/\.[^.]+$/, '') || String(scanId);
      downloadBase64File(base64, `${baseName}.pdf`, MIME.PDF);
    } catch (e) {
      logger.error(e);
      notifications.error('acts:errors.loadScanFailed');
    }
  };

  /**
   * OSI-763: пользователь согласился скачать печатную форму вместо подписанного файла.
   *
   * Форму берём ИЗ КЭША печатной формы (`loadPrintForm`), а НЕ из `signedActsDialog.doc`:
   * с переходом просмотра на оригинал в `doc.base64` теперь может лежать подписанный
   * документ из скана, и кнопка «Сохранить без ЭЦП» отдала бы совсем не то, что обещает.
   * Практически кэш здесь всегда заполнен — подтверждение открывается только при
   * `scanId === null`, а в этом случае `onShowSignedActClick` уже загрузил форму
   * в просмотр и в кэш. Ветка с запросом оставлена как страховка на случай,
   * если состояние соберётся иначе.
   *
   * Имя файла намеренно НЕ совпадает с именем подписанного акта (`<actNum>.pdf`):
   * в папке «Загрузки» два одноимённых файла спутать легко, а юридическую силу имеет
   * только один из них. Поэтому к номеру акта добавляем локализованный суффикс.
   */
  onConfirmDownloadPrintForm = async (): Promise<void> => {
    const { act, scanFileName, scanId } = this.signedActsDialog;

    const base64 = await this.loadPrintForm();

    if (!base64) {
      // Об ошибке (или о завершённой сессии — молча) уже сообщил loadPrintForm.
      this.onCancelDownloadPrintForm();
      return;
    }

    const baseName = act?.actNum || scanFileName.replace(/\.[^.]+$/, '') || String(scanId ?? '');
    const suffix = i18next.t('acts:signedActs.printFormFileSuffix');

    downloadBase64File(base64, `${baseName} ${suffix}.pdf`, MIME.PDF);

    this.onCancelDownloadPrintForm();
  };

  /** OSI-763: отказ от скачивания печатной формы — закрываем только подтверждение,
   *  основной диалог с формой остаётся открытым. */
  onCancelDownloadPrintForm = (): void => {
    this.signedActsDialog.isNoScanConfirmOpen = false;
  };

  onSignActClick = async (actId: number): Promise<void> => {
    try {
      const act = this.notSignedActs?.find((item) => item.id === actId);

      if (!act) {
        notifications.error('acts:errors.actNotFound');
        return;
      }

      // OSI-818: акт входит в общий акт по БИН/ИИН — отдельно его подписать нельзя,
      // открываем подписание общего акта целиком.
      if (act.combinedActId) {
        await this.onOpenCombinedAct(act.combinedActId);
        return;
      }

      const data = await getWorkCompletionAct(act.id);

      // OSI-763: на 401 запрос резолвится undefined (см. isSessionGone) — без этой проверки
      // поверх «Сессия истекла» выскочил бы второй тост про ошибку загрузки формы.
      if (isSessionGone()) return;

      if (!data?.pdfBase64) {
        notifications.error('acts:errors.loadFormFailed');
        return;
      }

      // OSI-763: на подпись идёт чистый PDF без блока расшифровки ЭЦП — он появляется
      // в печатной форме уже после подписания. Поэтому диалог ЭЦП всегда ровно один.
      runInAction(() => {
        this.notSignedActsDialog = {
          doc: { fileName: '', base64: data.pdfBase64 },
          isOpen: true,
          act
        };
      });
    } catch (e) {
      logger.error(e);
      notifySignError(e, 'acts:errors.loadFormFailed');
    }
  };

  /**
   * OSI-763: сохранение подписанного акта. Подписание однофазное — SignerBasic сам
   * подписывает PDF из диалога и отдаёт сюда готовый CMS-контейнер. Ни challenge,
   * ни сверки сертификата (код -10) больше нет: блок расшифровки ЭЦП печатается
   * не в подписываемом документе, а в печатной форме уже подписанного акта.
   */
  onPostSign = async (cms: string): Promise<void> => {
    const actId = this.notSignedActsDialog.act?.id;

    if (!actId) {
      notifications.error('acts:errors.actNotFound');
      return;
    }

    try {
      await saveSignedWorkCompletionAct(actId, cms);

      // OSI-763: КРИТИЧНО. На 401 сохранение резолвится undefined без throw (см. isSessionGone),
      // и без этой проверки пользователь увидел бы зелёное «Акт успешно подписан», хотя акт
      // не подписан. Выходим молча ДО тоста успеха и до перезагрузки списков — сессии уже нет,
      // эти запросы всё равно вернут 401.
      if (isSessionGone()) return;

      notifications.success('acts:notSignedActs.sign.success');

      await this.osiModule.refreshOsi();
      await Promise.allSettled([this.loadNotSignedActs(), this.loadSignedActs()]);

      this.onCloseNotSignedActsDialog();
    } catch (e) {
      logger.error(e);

      // OSI-763: сессия завершена по 401 — тост и редирект уже сделал интерцептор,
      // второй тост об ошибке подписания только запутал бы. Диалог всё равно закрываем:
      // акт не подписан, а форма без сессии бесполезна.
      // (notifySignError молчит и сам, но явная ветка не даёт потерять это при рефакторинге.)
      if (isSessionGone()) {
        this.onCloseNotSignedActsDialog();
        return;
      }

      notifySignError(e, 'acts:errors.signFailed');
      this.onCloseNotSignedActsDialog();
    }
  };

  onCloseNotSignedActsDialog = () => {
    this.notSignedActsDialog = { ...initialNotSignedActsDialog };
  };

  /**
   * OSI-818: общий акт по id — сначала из загруженного списка, иначе с бэкенда
   * (`GET /CombinedActs/{id}`): список мог устареть или не загрузиться.
   * Возвращает null, если акт получить не удалось; о причине пользователь уведомлён
   * (при завершённой сессии — молча, тост уже показал интерцептор).
   */
  private findCombinedAct = async (combinedActId: number): Promise<CombinedAct | null> => {
    const fromList = this.combinedActs.find((item) => item.id === combinedActId);

    if (fromList) return fromList;

    const fetched = await getCombinedActById(combinedActId);

    if (isSessionGone()) return null;

    if (!fetched) {
      notifications.error('acts:errors.combinedActNotFound');
      return null;
    }

    return fetched;
  };

  /**
   * OSI-818: открыть общий акт.
   *  - SIGNED — просмотр подписанного документа (через акт своего дома);
   *  - CREATED — загрузка PDF общего акта и диалог подписания.
   */
  onOpenCombinedAct = async (combinedActId: number): Promise<void> => {
    // Запрос PDF долгий — повторные клики, пока он идёт, игнорируем.
    if (this.openingCombinedActId !== null) return;

    this.openingCombinedActId = combinedActId;

    try {
      const combinedAct = await this.findCombinedAct(combinedActId);

      if (!combinedAct) return;

      if (combinedAct.stateCode === CombinedActStateCodes.SIGNED) {
        // Общий акт уже подписан (например, пользователем другого дома группы), а списки
        // на странице устарели — акта своего дома среди подписанных ещё нет. Обновляем.
        const ownItem = combinedAct.items?.find((item) => item.osiId === this.currentOsiId);
        if (ownItem && !this.signedActs?.some((item) => item.id === ownItem.actId)) {
          await this.reloadAllActs();
          if (isSessionGone()) return;
        }

        await this.onShowSignedCombinedAct(combinedAct);
        return;
      }

      const data = await getCombinedActForm(combinedAct.id);

      // На 401 запрос резолвится undefined (см. isSessionGone) — молчим.
      if (isSessionGone()) return;

      if (!data?.pdfBase64) {
        notifications.error('acts:errors.loadCombinedFormFailed');
        return;
      }

      runInAction(() => {
        this.combinedActDialog = {
          isOpen: true,
          combinedAct,
          doc: { base64: data.pdfBase64 }
        };
      });
    } catch (e) {
      logger.error(e);
      notifySignError(e, 'acts:errors.loadCombinedFormFailed');
    } finally {
      runInAction(() => {
        this.openingCombinedActId = null;
      });
    }
  };

  /**
   * OSI-818: сохранение подписанного общего акта. Копия `onPostSign` со всеми её
   * страховками (401 → молча, без ложного «подписан»; ошибка бэкенда — тостом как есть),
   * но PUT уходит на `/doc/api/act/combined/{id}/sign`, и перезагружаются все три списка:
   * акт дома уходит из неподписанных, общий акт — в подписанные.
   */
  onPostSignCombined = async (cms: string): Promise<void> => {
    const combinedActId = this.combinedActDialog.combinedAct?.id;

    if (!combinedActId) {
      notifications.error('acts:errors.combinedActNotFound');
      return;
    }

    try {
      await saveSignedCombinedAct(combinedActId, cms);

      // КРИТИЧНО (как в onPostSign): на 401 сохранение резолвится undefined без throw —
      // без проверки пользователь увидел бы ложное «подписан».
      if (isSessionGone()) return;

      notifications.success('acts:combinedAct.sign.success');

      await this.osiModule.refreshOsi();
      await this.reloadAllActs();

      this.onCloseCombinedActDialog();
    } catch (e) {
      logger.error(e);

      // Сессия завершена по 401 — тост и редирект уже сделал интерцептор.
      if (isSessionGone()) {
        this.onCloseCombinedActDialog();
        return;
      }

      notifySignError(e, 'acts:errors.signCombinedFailed');
      this.onCloseCombinedActDialog();
    }
  };

  onCloseCombinedActDialog = () => {
    this.combinedActDialog = { ...initialCombinedActDialog };
  };

  /**
   * OSI-818: просмотр подписанного общего акта. У каждого акта состава SIGNED_ACT-документ
   * указывает на ОДИН общий скан, поэтому работает существующий путь просмотра подписанного
   * акта. Берём акт ИМЕННО текущего дома, а не первый из состава: `GET /Acts/{id}/docs`
   * фильтрует доступ по дому, и по чужому акту документов бы не вернулось.
   */
  onShowSignedCombinedAct = async (combinedActOrId: CombinedAct | number): Promise<void> => {
    const combinedAct =
      typeof combinedActOrId === 'number'
        ? this.combinedActs.find((item) => item.id === combinedActOrId)
        : combinedActOrId;

    const ownItem = combinedAct?.items?.find((item) => item.osiId === this.currentOsiId);

    if (!ownItem) {
      notifications.error('acts:errors.combinedActNotFound');
      return;
    }

    await this.onShowSignedActClick(ownItem.actId);
  };
}
